function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { white, gray50, gray100, gray200, gray400, gray600, gray800, red600, focusShadow, primary600 } from '../../theme/colors';
import { size4, size8, size14, size80, CONTROL_SIZE_MAP, size12, size64, size32 } from '../../theme/scale';
import { duration150, easeInOut } from '../../theme/animation';
import { fontRegular } from '../../theme/fonts';
import InputWrapper from '../input-wrapper';
import { usePasswordToggle } from './ShowPasswordProvider';
import Icon from '../../icon';
const PADDING_MAP = {
  sm: size64,
  md: size64,
  lg: size80
};
const ToggleButton = styled.button`
  position: absolute;
  right: 0;
  font-size: ${size12};
  ${({
  $size
}) => $size && {
  ...CONTROL_SIZE_MAP[$size]
}}
  background: none;
  border: none;
  color: ${primary600};
  border-radius: ${size4};

  &:focus {
    background: ${white};
    border-color: ${gray200};
    box-shadow: 0 0 0 3px ${focusShadow};
    outline: none;
  }
`;
const TextInputWrapper = styled.div`
  position: relative;
`;
const TextInputIconWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: absolute;
  top: 0;
  left: 10px;
`;
const Input = styled.input`
  ${({
  $size
}) => $size && {
  ...CONTROL_SIZE_MAP[$size]
}}
  border: 1px solid;
  border-color: ${({
  hasErrorMessage
}) => hasErrorMessage ? red600 : gray400};
  border-radius: ${size4};
  color: ${gray800};
  font-family: ${fontRegular};
  transition: border ${duration150} ${easeInOut},
    box-shadow ${duration150} ${easeInOut};
  width: 100%;
  ${({
  resize
}) => resize ? 'resize: vertical;' : 'resize: none;'}
  ${({
  multiLine
}) => multiLine && `height: unset; padding: ${size8} ${size14};`}
  ${({
  showPasswordToggle,
  $size
}) => showPasswordToggle && `padding-right: ${PADDING_MAP[$size]};`}
  ${({
  hasPrefixIcon
}) => hasPrefixIcon && `padding-left: ${size32};`}

  &::placeholder {
    color: ${gray600};
  }

  &:hover {
    background: ${gray50};
  }

  &:focus {
    background: ${white};
    border-color: ${({
  hasErrorMessage
}) => hasErrorMessage ? red600 : gray400};
    box-shadow: 0 0 0 3px ${focusShadow};
    outline: none;
  }

  &[readonly],
  &[disabled] {
    background: ${gray100};
    cursor: not-allowed;
  }
`;
export const TextField = /*#__PURE__*/forwardRef(({
  value,
  onChange,
  onBlur,
  nativeOnChange,
  size = 'md',
  id,
  placeholder,
  label,
  hint,
  errorMessage,
  isErrorSpacePreserved,
  type: initialType = 'text',
  multiLine = false,
  resize = false,
  hideLabel = false,
  smallLabel = false,
  caption,
  showPasswordToggle = false,
  showPasswordText = 'Show',
  hidePasswordText = 'Hide',
  disabled = false,
  prefixIcon,
  prefixIconColor,
  autoComplete,
  labelAriaDescribedby,
  captionAriaDescribedby,
  hintAriaDescribedby,
  errorMessageAriaDescribedby,
  ...props
}, ref) => {
  const {
    showPassword,
    togglePassword
  } = usePasswordToggle();
  const type = useMemo(() => {
    if (initialType === 'password' && showPassword) {
      return 'text';
    }
    return initialType;
  }, [initialType, showPassword]);
  const ariaDescribedby = useMemo(() => {
    const descriptions = [labelAriaDescribedby, captionAriaDescribedby, hintAriaDescribedby, errorMessageAriaDescribedby].filter(Boolean).join(' ');
    return descriptions || undefined;
  }, [labelAriaDescribedby, captionAriaDescribedby, hintAriaDescribedby, errorMessageAriaDescribedby]);
  return /*#__PURE__*/React.createElement(InputWrapper, {
    id,
    label,
    hint,
    errorMessage,
    isErrorSpacePreserved,
    hideLabel,
    caption,
    smallLabel,
    labelAriaDescribedby,
    captionAriaDescribedby,
    hintAriaDescribedby,
    errorMessageAriaDescribedby
  }, /*#__PURE__*/React.createElement(TextInputWrapper, null, prefixIcon && /*#__PURE__*/React.createElement(TextInputIconWrapper, null, /*#__PURE__*/React.createElement(Icon, {
    size: 18,
    type: prefixIcon,
    color: prefixIconColor
  })), /*#__PURE__*/React.createElement(Input, _extends({
    resize,
    multiLine,
    type,
    id,
    placeholder,
    showPasswordToggle,
    disabled,
    ref,
    value,
    autoComplete,
    $size: size,
    onChange: event => {
      if (nativeOnChange) nativeOnChange(event);
      if (onChange) onChange(event.target.value);
    },
    onBlur: event => {
      if (onBlur) onBlur(event.target.value);
    },
    as: multiLine ? 'textarea' : 'input',
    hasErrorMessage: !!errorMessage,
    hasPrefixIcon: !!prefixIcon,
    "aria-describedby": ariaDescribedby
  }, props)), initialType === 'password' && showPasswordToggle && /*#__PURE__*/React.createElement(ToggleButton, {
    type: "button",
    $size: size,
    onClick: togglePassword
  }, showPassword ? hidePasswordText : showPasswordText)));
});
TextField.displayName = 'TextField';
export default TextField;
//# sourceMappingURL=index.js.map